import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Footer from "partials/Footer";
import Breadcrumb, { BreadcrumbItem } from "components/Breadcrumb";
import Button from "components/Button";
import Input from "components/form/Input";
import Textarea from "components/form/Textarea";

const BASE_URL = "https://api.kennapartners.com/api/v1"; // Base URL for all API calls

// ─── Human-readable field labels matching ProfileSchema ───────────────────────
const FIELD_LABELS = {
  full_name:     "Full Name",
  phone:         "Phone",
  email:         "Email",
  role:          "Role",
  bio:           "Bio",
  experience:    "Experience",
  publications:  "Publications",
  education:     "Education",
  bars:          "Bar Admissions",
  certifications:"Certifications",
  practice_areas:"Practice Areas",
  books:         "Books",
  memberships:   "Memberships",
};

// ─── Parse FastAPI / Pydantic validation errors into { field: message } ───────
const parseBackendError = async (response) => {
  let body;
  try { body = await response.clone().json(); } catch { body = null; }

  const backendMsg = body?.detail
    ? (Array.isArray(body.detail) ? null : String(body.detail))
    : (body?.message ? String(body.message) : null);

  switch (response.status) {
    case 422: {
      if (body?.detail && Array.isArray(body.detail)) {
        const fieldMap = {};
        const general  = [];
        body.detail.forEach((err) => {
          const loc   = err.loc || [];
          const field = loc.find((l) => typeof l === "string" && l !== "body");
          const label = field ? (FIELD_LABELS[field] || field) : null;
          let msg = err.msg || "Invalid value";
          msg = msg.replace(/^value_error\./i, "").replace(/_/g, " ");
          msg = msg.charAt(0).toUpperCase() + msg.slice(1);
          if (label) { fieldMap[field] = `${label}: ${msg}`; }
          else        { general.push(msg); }
        });
        const summary = Object.keys(fieldMap).length
          ? `Please fix the highlighted fields: ${Object.keys(fieldMap).map(f => FIELD_LABELS[f] || f).join(", ")}.`
          : general.join(" \u00b7 ") || "Some fields are invalid. Please review and try again.";
        return { fieldErrors: fieldMap, general: summary };
      }
      return { fieldErrors: {}, general: backendMsg || "The submitted data is invalid. Please check all fields and try again." };
    }
    case 409: {
      const msg = backendMsg || "A profile with this email address already exists. Please use a different email.";
      return { fieldErrors: { email: msg }, general: msg };
    }
    case 401:
      return { fieldErrors: {}, general: backendMsg || "Your session has expired. Please log out and log in again to continue." };
    case 403:
      return { fieldErrors: {}, general: backendMsg || "You do not have permission to perform this action. Contact your administrator if you believe this is a mistake." };
    case 404:
      return { fieldErrors: {}, general: backendMsg || "The requested profile could not be found. It may have been deleted." };
    case 413:
      return { fieldErrors: { _file: "The file you selected is too large. Please upload an image under 2 MB." }, general: "Upload rejected: file exceeds the maximum allowed size of 2 MB." };
    case 415:
      return { fieldErrors: { _file: "This file type is not supported. Please upload a PNG, JPEG, JPG, or GIF." }, general: "Upload rejected: unsupported file type. Accepted formats are PNG, JPEG, JPG, and GIF." };
    case 429:
      return { fieldErrors: {}, general: backendMsg || "Too many requests. Please wait a moment before trying again." };
    case 500:
      return { fieldErrors: {}, general: backendMsg || "An internal server error occurred. Our team has been notified. Please try again in a few minutes." };
    case 502:
      return { fieldErrors: {}, general: "The server is temporarily unavailable (bad gateway). Please try again shortly." };
    case 503:
      return { fieldErrors: {}, general: "The service is currently unavailable or undergoing maintenance. Please try again later." };
    case 504:
      return { fieldErrors: {}, general: "The request timed out waiting for the server. Please check your connection and try again." };
    default: {
      const msg = backendMsg || `An unexpected error occurred (code ${response.status}). Please try again or contact support if the problem persists.`;
      return { fieldErrors: {}, general: msg };
    }
  }
};
// ─── Toast notification component ─────────────────────────────────────────────
const Toast = ({ toasts, onDismiss }) => {
  if (!toasts.length) return null;
  return (
    <div style={{ position:"fixed", top:"20px", left:"50%", transform:"translateX(-50%)", zIndex:99999, display:"flex", flexDirection:"column", gap:"10px", width:"calc(100% - 32px)", maxWidth:"520px" }}>
      {toasts.map((t) => (
        <div key={t.id} style={{
          display:"flex", alignItems:"flex-start", gap:"12px",
          backgroundColor: t.type === "success" ? "#16a34a" : t.type === "warning" ? "#d97706" : "#dc2626",
          color:"white", fontSize:"14px", fontWeight:"500",
          padding:"14px 16px", borderRadius:"10px",
          boxShadow:"0 8px 32px rgba(0,0,0,0.3)",
          animation:"toastIn 0.25s ease",
        }}>
          <span style={{fontSize:"18px",lineHeight:"1.2",flexShrink:0}}>
            {t.type === "success" ? "✓" : t.type === "warning" ? "⚠️" : "✕"}
          </span>
          <div style={{flex:1,lineHeight:"1.5"}}>{t.message}</div>
          <button onClick={() => onDismiss(t.id)} style={{marginLeft:"4px",background:"none",border:"none",color:"white",fontSize:"20px",lineHeight:"1",cursor:"pointer",flexShrink:0,fontWeight:"bold",padding:0}}>×</button>
        </div>
      ))}
      <style>{`@keyframes toastIn{from{opacity:0;transform:translateY(-8px)}to{opacity:1;transform:translateY(0)}}`}</style>
    </div>
  );
};

// ─── Inline field-level error message ─────────────────────────────────────────
const FieldError = ({ message }) =>
  message ? (
    <p style={{color:"#dc2626",fontSize:"12px",marginTop:"4px",display:"flex",alignItems:"center",gap:"4px"}}>
      <span>⚠</span> {message}
    </p>
  ) : null;

const Profiles = () => {
  const [profiles, setProfiles] = useState([]);
  const [filteredProfiles, setFilteredProfiles] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [roles, setRoles] = useState([]);
  const [practiceAreas, setPracticeAreas] = useState([]);
  const [booksList, setBooksList] = useState([]);
const [currentPage, setCurrentPage] = useState(
   () => Number(localStorage.getItem('profile_page')) || 1
 );
const [totalPages, setTotalPages] = useState(1);
const [totalProfiles, setTotalProfiles] = useState(0);
const [limit, setLimit] = useState(
   () => Number(localStorage.getItem('profile_limit')) || 10
);

  // ─── Toast & field-error state ──────────────────────────────────────────────
  const [toasts, setToasts] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});

  const pushToast = (message, type = "error", duration = 6000) => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, message, type }]);
    if (duration) setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), duration);
  };
  const dismissToast = (id) => setToasts((prev) => prev.filter((t) => t.id !== id));
  const clearFieldErrors = () => setFieldErrors({});

  // helper: apply errors from parseBackendError result
  const applyBackendErrors = ({ fieldErrors: fe, general }) => {
    if (fe && Object.keys(fe).length) setFieldErrors(fe);
    if (general) pushToast(general, "error");
    else if (fe && Object.keys(fe).length) {
      const msgs = Object.values(fe).join(" · ");
      pushToast(msgs, "error");
    }
  };

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [file, setFile] = useState(null);

  // Current profile being operated on
  const [currentProfile, setCurrentProfile] = useState(null);
  const [selectedProfileId, setSelectedProfileId] = useState(null);

  // Form data
const [formData, setFormData] = useState({
  full_name: "",
  phone: "",
  email: "",
  role: { id: "", name: "" },
  practice_areas: [], // Now will store strings directly
  bio: "",
  experience: [],
  publications: [],
  education: [],
  bars: [],
  certifications: [],
  books: [],
  memberships: [],
});

  // Temporary input for array fields
  const [tempArrayInput, setTempArrayInput] = useState({
    practice_areas: "",
    experience: "",
    education: "",
    bars: "",
    publications: "",
    certifications: "",
    books: "",
    memberships: "",
  });

  const navigate = useNavigate();

useEffect(() => {
  fetchProfiles();
  fetchRoles();
  fetchPracticeAreas();
  fetchBooks();
}, [currentPage, limit]); // Re-fetch when page or limit changes

  useEffect(() => {
    // filterProfiles();
  }, [profiles, searchTerm]);

const fetchProfiles = async () => {
  setLoading(true);
  try {
    const token = localStorage.getItem("access_token");

    // “All” is mapped to 10 000 behind the scenes
    const pageParam = limit === 10000 ? 1 : currentPage;
    const actualLimit = limit === 10000 ? 10000 : limit;

    const response = await fetch(
      `${BASE_URL}/profiles/?page=${pageParam}&limit=${actualLimit}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    if (data && data.data && Array.isArray(data.data.profiles)) {
      setProfiles(data.data.profiles);
      setTotalProfiles(data.data.total_profiles || 0);
      setTotalPages(
        actualLimit === 10000
          ? 1
          : Math.ceil(data.data.total_profiles / actualLimit) || 1
      );
    } else {
      console.error(
        'Invalid data format. Expected an array of profiles under "data.profiles".'
      );
      setProfiles([]);
    }
    setError("");
  } catch (error) {
    console.error("Error fetching profiles:", error);
    setError("Failed to fetch profiles. Please try again.");
    setProfiles([]);
  } finally {
    setLoading(false);
  }
};

  const fetchBooks = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(`${BASE_URL}/books/`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setBooksList(data.data.book || []);
    } catch (error) {
      console.error("Error fetching books:", error);
    }
  };

// Replace the filterProfiles useEffect with this:
useEffect(() => {
  if (searchTerm.trim()) {
    const debounceTimer = setTimeout(() => {
      searchAllProfiles(searchTerm);
    }, 300);
    return () => clearTimeout(debounceTimer);
  } else {
    setFilteredProfiles(profiles);
  }
}, [searchTerm, profiles]);


const searchAllProfiles = async (term) => {
  setLoading(true);
  try {
    const token = localStorage.getItem("access_token");
    const response = await fetch(
      `${BASE_URL}/profiles/?page=1&limit=10000`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      }
    );

    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

    const data = await response.json();
    const allProfiles = data.data?.profiles || [];

    const filtered = allProfiles.filter(
      (profile) =>
        profile.full_name?.toLowerCase().includes(term.toLowerCase()) ||
        profile.email?.toLowerCase().includes(term.toLowerCase()) ||
        profile.role?.name?.toLowerCase().includes(term.toLowerCase())
    );

    setFilteredProfiles(filtered);
  } catch (error) {
    console.error("Error searching profiles:", error);
    setFilteredProfiles([]);
  } finally {
    setLoading(false);
  }
};

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "role") {
      const selectedRole = roles.find((role) => role.id === value);
      setFormData((prev) => ({
        ...prev,
        [name]: selectedRole || { id: "", name: "" },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleArrayInputChange = (e) => {
    const { name, value } = e.target;
    setTempArrayInput((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

const addArrayItem = (field) => {
  if (field === "books") {  // Only keep special handling for books
    const selectedId = tempArrayInput[field];
    if (!selectedId) return;

    const selectedItem = booksList.find((book) => book.id === selectedId);
    if (!selectedItem) return;

    const alreadyExists = formData[field].some(
      (item) => item.id === selectedId
    );
    if (alreadyExists) return;

    setFormData((prev) => ({
      ...prev,
      [field]: [...prev[field], selectedItem],
    }));
  } else {
    const value = tempArrayInput[field];
    if (!value || value.trim() === "") return;

    setFormData((prev) => ({
      ...prev,
      [field]: [...prev[field], value.trim()],
    }));
  }

  // Clear the input after adding
  setTempArrayInput((prev) => ({
    ...prev,
    [field]: "",
  }));
};

  const removeArrayItem = (field, index) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index),
    }));
  };

  const resetForm = () => {
    setFormData({
      full_name: "",
      phone: "",
      email: "",
      role: { id: "", name: "" },
      practice_areas: [],
      bio: "",
      experience: [],
      publications: [],
      education: [],
      bars: [],
      certifications: [],
      books: [],
      memberships: [],
    });
    setFile(null);
  };

  // Create Profile
  const handleCreateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    clearFieldErrors();

    const payload = {
      full_name:     formData.full_name,
      phone:         formData.phone,
      email:         formData.email,
      role:          formData.role.id,
      practice_areas:formData.practice_areas,
      bio:           formData.bio,
      experience:    formData.experience,
      publications:  formData.publications,
      education:     formData.education,
      bars:          formData.bars,
      certifications:formData.certifications,
      books:         formData.books.map((book) => book.id),
      memberships:   formData.memberships,
    };

    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(`${BASE_URL}/profiles/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const parsed = await parseBackendError(response);
        applyBackendErrors(parsed);
        return;
      }

      const data = await response.json();
      const newProfile = data.data?.profile;

      if (!newProfile || !newProfile.id) {
        pushToast("Profile was created but the server returned an unexpected response.", "warning");
        setShowCreateModal(false);
        resetForm();
        fetchProfiles();
        return;
      }

      setProfiles((prev) => [...prev, newProfile]);

      // Upload profile picture if selected
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        const uploadResponse = await fetch(`${BASE_URL}/profiles/${newProfile.id}/upload`, {
          method: "PATCH",
          headers: { ...(token && { Authorization: `Bearer ${token}` }) },
          body: fd,
        });
        if (!uploadResponse.ok) {
          const parsed = await parseBackendError(uploadResponse);
          pushToast(`Profile created but image upload failed: ${parsed.general || "Unknown error"}`, "warning");
        } else {
          const updatedProfile = await uploadResponse.json();
          setProfiles((prev) =>
            prev.map((p) => (p.id === updatedProfile.id ? updatedProfile : p))
          );
        }
      }

      setShowCreateModal(false);
      resetForm();
      setFile(null);
      setError("");
      pushToast("Profile created successfully!", "success", 4000);
      fetchProfiles();
    } catch (error) {
      console.error("Error:", error);
      pushToast(error.message || "Failed to create profile. Please check your connection and try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  // Get Single Profile Details
  const fetchProfileDetails = async (id) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(`${BASE_URL}/profiles/${id}`, {
        headers: { ...(token && { Authorization: `Bearer ${token}` }) },
      });

      if (!response.ok) {
        const parsed = await parseBackendError(response);
        pushToast(parsed.general || "Failed to load profile details.", "error");
        setCurrentProfile(null);
        return;
      }

      const data = await response.json();
      const profileData = data.profile || data.data?.profile || data;
      if (!profileData) {
        pushToast("No profile data returned by the server.", "warning");
        setCurrentProfile(null);
        return;
      }
      setCurrentProfile(profileData);
      setError("");
    } catch (error) {
      console.error("Error fetching profile details:", error);
      pushToast("Failed to fetch profile details. Please check your connection.", "error");
      setCurrentProfile(null);
    } finally {
      setLoading(false);
    }
  };

  // Update Profile
const handleUpdateProfile = async (e) => {
  e.preventDefault();
  setLoading(true);
  clearFieldErrors();

  try {
    const token = localStorage.getItem("access_token");
    const payload = {
      full_name:     formData.full_name,
      phone:         formData.phone,
      email:         formData.email,
      role:          formData.role.id,
      practice_areas:formData.practice_areas,
      bio:           formData.bio,
      experience:    formData.experience,
      publications:  formData.publications,
      education:     formData.education,
      bars:          formData.bars,
      certifications:formData.certifications,
      memberships:   formData.memberships,
      books:         formData.books.map((book) => book.id),
    };

    const response = await fetch(`${BASE_URL}/profiles/${currentProfile.id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const parsed = await parseBackendError(response);
      applyBackendErrors(parsed);
      return;
    }

    const data = await response.json();
    let updatedProfile = data.data?.profile || data.profile || data;

    // Handle file upload if a new file was selected
    if (file) {
      const fd = new FormData();
      fd.append("file", file);
      const uploadResponse = await fetch(`${BASE_URL}/profiles/${currentProfile.id}/upload`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!uploadResponse.ok) {
        const parsed = await parseBackendError(uploadResponse);
        pushToast(`Profile updated but image upload failed: ${parsed.general || "Unknown error"}`, "warning");
        if (parsed.fieldErrors?._file) setFieldErrors((prev) => ({ ...prev, _file: parsed.fieldErrors._file }));
      } else {
        const uploadData = await uploadResponse.json();
        updatedProfile = { ...updatedProfile, file_url: uploadData.data?.file_url || uploadData.file_url };
      }
    }

    setProfiles((prev) => prev.map((p) => (p.id === updatedProfile.id ? updatedProfile : p)));
    setCurrentProfile(updatedProfile);
    setShowEditModal(false);
    setFile(null);
    pushToast("Profile updated successfully!", "success", 4000);
    fetchProfiles();
  } catch (error) {
    console.error("Update error:", error);
    pushToast(error.message || "Failed to update profile. Please check your connection and try again.", "error");
  } finally {
    setLoading(false);
  }
};

  // Delete Profile
  const handleDeleteProfile = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(`${BASE_URL}/profiles/${selectedProfileId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        const parsed = await parseBackendError(response);
        pushToast(parsed.general || "Failed to delete profile.", "error");
        setShowDeleteModal(false);
        return;
      }

      setProfiles((prev) => prev.filter((profile) => profile.id !== selectedProfileId));
      setShowDeleteModal(false);
      setSelectedProfileId(null);
      setError("");
      pushToast("Profile deleted successfully.", "success", 4000);
    } catch (error) {
      console.error("Error deleting profile:", error);
      pushToast("Failed to delete profile. Please check your connection and try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  // Upload File
  const handleFileUpload = async (e) => {
    e.preventDefault();
    if (!file) {
      pushToast("Please select a file before uploading.", "warning");
      return;
    }

    const fd = new FormData();
    fd.append("file", file);

    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(`${BASE_URL}/profiles/${selectedProfileId}/upload`, {
        method: "PATCH",
        headers: { ...(token && { Authorization: `Bearer ${token}` }) },
        body: fd,
      });

      if (!response.ok) {
        const parsed = await parseBackendError(response);
        if (parsed.fieldErrors?._file) setFieldErrors({ _file: parsed.fieldErrors._file });
        pushToast(parsed.general || "File upload failed.", "error");
        return;
      }

      const data = await response.json();
      setProfiles((prev) =>
        prev.map((profile) =>
          profile.id === selectedProfileId
            ? { ...profile, file_url: data.data.file_url }
            : profile
        )
      );
      if (currentProfile && currentProfile.id === selectedProfileId) {
        setCurrentProfile((prev) => ({ ...prev, file_url: data.data.file_url }));
      }
      setShowUploadModal(false);
      setFile(null);
      pushToast("Profile image uploaded successfully!", "success", 4000);
    } catch (error) {
      console.error("Upload error:", error);
      pushToast("File upload failed. Please check your connection and try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  // Modal handlers
  const openCreateModal = () => {
    resetForm();
    clearFieldErrors();
    setShowCreateModal(true);
  };

const openEditModal = (profile) => {
  setCurrentProfile(profile);
  setFormData({
    full_name: profile.full_name || "",
    phone: profile.phone || "",
    email: profile.email || "",
    role: profile.role || { id: "", name: "" },
    bio: profile.bio || "",
    experience: profile.experience || [],
    publications: profile.publications || [],
    education: profile.education || [],
    bars: profile.bars || [],
    certifications: profile.certifications || [],
    books: Array.isArray(profile.books)
      ? profile.books.map((book) =>
          typeof book === "object" ? book : { id: book, name: "" }
        )
      : [],
    memberships: profile.memberships || [],
    practice_areas: profile.practice_areas || [], // Now directly using the array of strings
  });
  setShowEditModal(true);
};

  const openDeleteModal = (profileId) => {
    setSelectedProfileId(profileId);
    setShowDeleteModal(true);
  };

  const openDetailsModal = async (profileId) => {
    setSelectedProfileId(profileId);
    await fetchProfileDetails(profileId);
    setShowDetailsModal(true);
  };

  const openUploadModal = (profileId) => {
    setSelectedProfileId(profileId);
    setShowUploadModal(true);
  };

  const closeAllModals = () => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setShowDeleteModal(false);
    setShowDetailsModal(false);
    setShowUploadModal(false);
    setCurrentProfile(null);
    setSelectedProfileId(null);
    clearFieldErrors();
    resetForm();
  };

  //fetch practice areas
  const fetchPracticeAreas = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(`${BASE_URL}/practice-areas/`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setPracticeAreas(data.data.practice_areas || []);
    } catch (error) {
      console.error("Error fetching practice areas:", error);
    }
  };

  //Fetch Roles
  const fetchRoles = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(`${BASE_URL}/roles/`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setRoles(data.data.roles || []);
    } catch (error) {
      console.error("Error fetching roles:", error);
    }
  };

  // Render array items as chips
  const renderArrayItems = (items) => {
    if (!items || !Array.isArray(items)) return null;

    return items.map((item, index) => (
      <span
        key={index}
        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mr-1 mb-1"
      >
        {typeof item === "object" ? item.name : item}
      </span>
    ));
  };

  // Render books specifically since they have a different structure
  const renderBooks = (books) => {
    if (!books || !Array.isArray(books)) return null;

    return books.map((book, index) => (
      <div key={index} className="mb-2">
        <p className="font-medium">{book.name || book}</p>
        {book.author && (
          <p className="text-sm text-gray-600">by {book.author}</p>
        )}
        {book.file_url && (
          <a
            href={book.file_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline text-sm"
          >
            View Book
          </a>
        )}
      </div>
    ));
  };

  // Render practice areas
const renderPracticeAreas = (areas) => {
  if (!areas || !Array.isArray(areas)) return null;

  return areas.map((area, index) => (
    <span
      key={index}
      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 mr-1 mb-1"
    >
      {area}
    </span>
  ));
};


const handleLimitChange = (e) => {
  const newLimit = Number(e.target.value);
  setLimit(newLimit);
  localStorage.setItem('profile_limit', newLimit);
  setCurrentPage(1); // Reset to first page when changing limit
  // The useEffect with [currentPage, limit] dependency will trigger a refetch
};

const handlePageChange = (page) => {
  setCurrentPage(page);
  localStorage.setItem('profile_page', page);
};

const renderPagination = () => {
  let pages = [];
  for (let i = 1; i <= totalPages; i++) {
    pages.push(
      <button
        key={i}
        onClick={() => handlePageChange(i)}
        className={`px-3 py-1 rounded-md ${currentPage === i ? 'bg-blue-600 text-white' : 'bg-gray-200 hover:bg-gray-300'}`}
      >
        {i}
      </button>
    );
  }
  return pages;
};

  return (
    <main className="workspace">
      {/* ─── Global Toast Notifications ─────────────────────────────────── */}
      <Toast toasts={toasts} onDismiss={dismissToast} />

      {/* Legacy inline banners kept for page-level fetch errors */}
      {error && (
        <div className="alert-danger mb-4">
          {error}
          <button className="float-right text-white" onClick={() => setError("")}>×</button>
        </div>
      )}

      <Breadcrumb title="Profiles">
        <BreadcrumbItem link="#no-link">Pages</BreadcrumbItem>
        <BreadcrumbItem>Profiles</BreadcrumbItem>
      </Breadcrumb>

      {/* Header with Actions */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Profile Management</h1>
        <div className="flex gap-2">
          <button onClick={openCreateModal} className="btn-primary">
            <span className="la la-plus"></span> Add New Profile
          </button>
          <button
            onClick={fetchProfiles}
            className="btn-secondary"
            disabled={loading}
          >
            <span className="la la-refresh"></span> Refresh
          </button>
        </div>
      </div>

 {/* Search and Pagination Controls */}
      <div className="card mb-6">
        <div className="card-body">
          <div className="flex flex-col md:flex-row gap-4 justify-between">
            <div className="flex-1 min-w-64">
              <input
                type="text"
                placeholder="Search profiles by name, email or role..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-control"
              />
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center">
                <label className="mr-2 text-sm text-gray-600">Show:</label>
                <select
                  value={limit}
                  onChange={handleLimitChange}
                  className="form-control w-20"
                >
 <option value="10">10</option>
 <option value="20">20</option>
 <option value="50">50</option>
 <option value="10000">All</option>
                </select>
              </div>
<div className="text-sm text-gray-600">
  {limit === 10000
    ? `Showing all ${totalProfiles} profiles`
    : `Showing ${(currentPage - 1) * limit + 1}-${Math.min(
        currentPage * limit,
        totalProfiles
      )} of ${totalProfiles} profiles`}
</div>
            </div>
          </div>
        </div>
      </div>

  {/* Profiles Table */}
      <div className="card">
        <div className="card-body p-0">
          {loading && !profiles.length ? (
            <div className="text-center py-8">
              <span className="la la-spinner la-spin text-2xl"></span>
              <p className="mt-2">Loading profiles...</p>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-hover w-full">
                  <thead>
                    <tr>
                      <th className="w-1/4">Name</th>
                      <th className="w-1/4">Email</th>
                      <th className="w-1/4">Role</th>
                      <th className="w-1/4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.isArray(filteredProfiles) &&
                    filteredProfiles.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="text-center py-8">
                          {profiles.length === 0
                            ? "No profiles found. Create your first profile!"
                            : "No profiles match your search criteria."}
                        </td>
                      </tr>
                    ) : (
                      filteredProfiles.map((profile) => (
                        <tr key={profile.id}>
                          <td className="w-1/4">
                            <div className="font-medium">{profile.full_name}</div>
                            <div className="text-sm text-muted">
                              {profile.phone}
                            </div>
                          </td>
                          <td className="w-1/4">{profile.email}</td>
                          <td className="w-1/4">
                            {profile.role?.designation || "No role"}
                          </td>
                          <td className="w-1/4">
                            <div className="flex gap-1">
                              <Button
                                onClick={() => openDetailsModal(profile.id)}
                                className="btn btn-sm btn-info"
                                title="View Details"
                              >
                                <span className="la la-eye"></span>
                              </Button>
                              <Button
                                onClick={() => openEditModal(profile)}
                                className="btn btn-sm btn-warning"
                                title="Edit Profile"
                              >
                                <span className="la la-edit"></span>
                              </Button>
                              <Button
                                color="danger"
                                onClick={() => openDeleteModal(profile.id)}
                                className="btn btn-sm btn-danger"
                                title="Delete Profile"
                              >
                                <span className="la la-trash"></span>
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination Controls */}
              {limit !== 0 && totalPages > 1 && (
                <div className="flex justify-between items-center p-4 border-t">
                  <div className="text-sm text-gray-600">
                    Page {currentPage} of {totalPages}
                  </div>
                  <div className="flex gap-1">
                    {renderPagination()}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div
          className="modal fade show fixed inset-0 overflow-y-auto"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={closeAllModals}
        >
          <div
            className="flex items-center justify-center min-h-screen"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-dialog modal-lg relative w-auto my-6 mx-auto max-w-3xl">
              <div className="modal-content relative bg-white rounded-lg shadow-lg">
                <div className="modal-header flex items-start justify-between p-6 border-b rounded-t">
                  <h5 className="modal-title text-xl font-semibold text-gray-800">
                    Create New Profile
                  </h5>
                  <button
                    onClick={closeAllModals}
                    className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center"
                  >
                    <span className="la la-times text-2xl"></span>
                  </button>
                </div>

                <form onSubmit={handleCreateProfile}>
                  <div className="modal-body p-6 space-y-6 max-h-[70vh] overflow-y-auto">
                    {/* Basic Info */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-medium text-gray-900">
                        Basic Information
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="form-group space-y-2">
                          <label className="block text-sm font-medium text-gray-700">
                            Full Name *
                          </label>
                          <Input
                            type="text"
                            name="full_name"
                            value={formData.full_name}
                            onChange={(e) => { handleInputChange(e); setFieldErrors((p) => { const n={...p}; delete n.full_name; return n; }); }}
                            className={`w-full p-3 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${fieldErrors.full_name ? "border-red-500 bg-red-50" : "border-gray-300"}`}
                          />
                          <FieldError message={fieldErrors.full_name} />
                        </div>

                        <div className="form-group space-y-2">
                          <label className="block text-sm font-medium text-gray-700">
                            Phone <span className="text-gray-400 font-normal text-xs">(optional)</span>
                          </label>
                          <Input
                            type="text"
                            name="phone"
                            value={formData.phone}
                            onChange={(e) => { handleInputChange(e); setFieldErrors((p) => { const n={...p}; delete n.phone; return n; }); }}
                            className={`w-full p-3 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${fieldErrors.phone ? "border-red-500 bg-red-50" : "border-gray-300"}`}
                          />
                          <FieldError message={fieldErrors.phone} />
                        </div>

                        <div className="form-group space-y-2">
                          <label className="block text-sm font-medium text-gray-700">
                            Email *
                          </label>
                          <Input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={(e) => { handleInputChange(e); setFieldErrors((p) => { const n={...p}; delete n.email; return n; }); }}
                            className={`w-full p-3 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${fieldErrors.email ? "border-red-500 bg-red-50" : "border-gray-300"}`}
                          />
                          <FieldError message={fieldErrors.email} />
                        </div>

                        <div className="form-group space-y-2">
                          <label className="block text-sm font-medium text-gray-700">
                            Role *
                          </label>
                          <select
                            name="role"
                            value={formData.role.id || ""}
                            onChange={(e) => { handleInputChange(e); setFieldErrors((p) => { const n={...p}; delete n.role; return n; }); }}
                            className={`w-full p-3 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${fieldErrors.role ? "border-red-500 bg-red-50" : "border-gray-300"}`}
                          >
                            <option value="">Select a role</option>
                            {roles.map((role) => (
                              <option key={role.id} value={role.id}>
                                {role.designation}
                              </option>
                            ))}
                          </select>
                          <FieldError message={fieldErrors.role} />
                        </div>
                      </div>
                    </div>

                    {/* Bio */}
                    <div className="form-group space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Bio *
                      </label>
                      <Textarea
                        name="bio"
                        value={formData.bio}
                        onChange={(e) => { handleInputChange(e); setFieldErrors((p) => { const n={...p}; delete n.bio; return n; }); }}
                        rows="6"
                        className={`w-full p-3 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${fieldErrors.bio ? "border-red-500 bg-red-50" : "border-gray-300"}`}
                      />
                      <FieldError message={fieldErrors.bio} />
                    </div>

                    {/* Array Fields */}
                    <div className="space-y-6">
                      <h3 className="text-lg font-medium text-gray-900">
                        Professional Information
                      </h3>

                 
{/* Practice Areas */}
<div className="form-group space-y-2">
  <label className="block text-sm font-medium text-gray-700">
    Practice Areas *
  </label>
  <div className={`flex gap-2 ${fieldErrors.practice_areas ? "rounded-md ring-1 ring-red-500" : ""}`}>
    <Input
      type="text"
      name="practice_areas"
      value={tempArrayInput.practice_areas || ""}
      onChange={handleArrayInputChange}
      placeholder="Add practice area"
      className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
    />
    <Button
      type="button"
      onClick={() => addArrayItem("practice_areas")}
      className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
    >
      Add
    </Button>
  </div>
  {/* Added practice areas display */}
  <div className="mt-2 flex flex-wrap gap-2">
    {formData.practice_areas.map((area, index) => (
      <span
        key={index}
        className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800"
      >
        {area}
        <button
          type="button"
          onClick={() => { removeArrayItem("practice_areas", index); }}
          className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full text-green-400 hover:bg-green-200 hover:text-green-500"
        >
          &times;
        </button>
      </span>
    ))}
  </div>
  <FieldError message={fieldErrors.practice_areas} />
</div>

                      {/* Experience */}
                      <div className="form-group space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Experience
                        </label>
                        <div className="flex gap-2">
                          <Input
                            type="text"
                            name="experience"
                            value={tempArrayInput.experience || ""}
                            onChange={handleArrayInputChange}
                            placeholder="Add experience item"
                            className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                          <Button
                            type="button"
                            onClick={() => addArrayItem("experience")}
                            className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                          >
                            Add
                          </Button>
                        </div>
                        <div className="mt-2">
                          {formData.experience.map((item, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mr-1 mb-1"
                            >
                              {item}
                              <button
                                type="button"
                                onClick={() => removeArrayItem("experience", index)}
                                className="ml-1 text-blue-600 hover:text-blue-900"
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                        <FieldError message={fieldErrors.experience} />
                      </div>

                      {/* Education */}
                      <div className="form-group space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Education
                        </label>
                        <div className="flex gap-2">
                          <Input
                            type="text"
                            name="education"
                            value={tempArrayInput.education || ""}
                            onChange={handleArrayInputChange}
                            placeholder="Add education item"
                            className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                          <Button
                            type="button"
                            onClick={() => addArrayItem("education")}
                            className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                          >
                            Add
                          </Button>
                        </div>
                        <div className="mt-2">
                          {formData.education.map((item, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mr-1 mb-1"
                            >
                              {item}
                              <button
                                type="button"
                                onClick={() => removeArrayItem("education", index)}
                                className="ml-1 text-blue-600 hover:text-blue-900"
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                        <FieldError message={fieldErrors.education} />
                      </div>

                      {/* Bars */}
                      <div className="form-group space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Bar Admissions *
                        </label>
                        <div className="flex gap-2">
                          <Input
                            type="text"
                            name="bars"
                            value={tempArrayInput.bars || ""}
                            onChange={handleArrayInputChange}
                            placeholder="Add bar admission"
                            className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                          <Button
                            type="button"
                            onClick={() => addArrayItem("bars")}
                            className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                          >
                            Add
                          </Button>
                        </div>
                        <div className="mt-2">
                          {formData.bars.map((item, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mr-1 mb-1"
                            >
                              {item}
                              <button
                                type="button"
                                onClick={() => removeArrayItem("bars", index)}
                                className="ml-1 text-blue-600 hover:text-blue-900"
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                        <FieldError message={fieldErrors.bars} />
                      </div>

                      {/* Publications */}
                      <div className="form-group space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Publications
                        </label>
                        <div className="flex gap-2">
                          <Input
                            type="text"
                            name="publications"
                            value={tempArrayInput.publications || ""}
                            onChange={handleArrayInputChange}
                            placeholder="Add publication"
                            className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                          <Button
                            type="button"
                            onClick={() => addArrayItem("publications")}
                            className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                          >
                            Add
                          </Button>
                        </div>
                        <div className="mt-2">
                          {formData.publications.map((item, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mr-1 mb-1"
                            >
                              {item}
                              <button
                                type="button"
                                onClick={() => removeArrayItem("publications", index)}
                                className="ml-1 text-blue-600 hover:text-blue-900"
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                        <FieldError message={fieldErrors.publications} />
                      </div>

                      {/* Certifications */}
                      <div className="form-group space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Certifications
                        </label>
                        <div className="flex gap-2">
                          <Input
                            type="text"
                            name="certifications"
                            value={tempArrayInput.certifications || ""}
                            onChange={handleArrayInputChange}
                            placeholder="Add certification"
                            className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                          <Button
                            type="button"
                            onClick={() => addArrayItem("certifications")}
                            className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                          >
                            Add
                          </Button>
                        </div>
                        <div className="mt-2">
                          {formData.certifications.map((item, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mr-1 mb-1"
                            >
                              {item}
                              <button
                                type="button"
                                onClick={() => removeArrayItem("certifications", index)}
                                className="ml-1 text-blue-600 hover:text-blue-900"
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                        <FieldError message={fieldErrors.certifications} />
                      </div>

                      {/* Books */}
                      <div className="form-group space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Books
                        </label>
                        <div className="flex gap-2">
                          <select
                            name="books"
                            value={tempArrayInput.books || ""}
                            onChange={handleArrayInputChange}
                            className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="">Select a book</option>
                            {booksList.map((book) => (
                              <option key={book.id} value={book.id}>
                                {book.name} by {book.author}
                              </option>
                            ))}
                          </select>
                          <Button
                            type="button"
                            onClick={() => addArrayItem("books")}
                            className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                          >
                            Add
                          </Button>
                        </div>
                        <div className="mt-2">
                          {formData.books.map((item, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mr-1 mb-1"
                            >
                              {item.name} by {item.author}
                              <button
                                type="button"
                                onClick={() => removeArrayItem("books", index)}
                                className="ml-1 text-blue-600 hover:text-blue-900"
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                        <FieldError message={fieldErrors.books} />
                      </div>

                      {/* Memberships */}
                      <div className="form-group space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Memberships *
                        </label>
                        <div className="flex gap-2">
                          <Input
                            type="text"
                            name="memberships"
                            value={tempArrayInput.memberships || ""}
                            onChange={handleArrayInputChange}
                            placeholder="Add membership"
                            className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                          <Button
                            type="button"
                            onClick={() => addArrayItem("memberships")}
                            className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                          >
                            Add
                          </Button>
                        </div>
                        <div className="mt-2">
                          {formData.memberships.map((item, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mr-1 mb-1"
                            >
                              {item}
                              <button
                                type="button"
                                onClick={() => removeArrayItem("memberships", index)}
                                className="ml-1 text-blue-600 hover:text-blue-900"
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                        <FieldError message={fieldErrors.memberships} />
                      </div>
                    </div>

                    {/* File Upload */}
                    <div className="form-group space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Upload Profile Picture
                      </label>
                      <input
                        type="file"
                        onChange={(e) => {
                          const f = e.target.files[0];
                          if (!f) return;
                          const allowed = ["image/png","image/jpeg","image/jpg","image/gif"];
                          if (!allowed.includes(f.type)) {
                            pushToast(`"${f.name}" is not a supported type. Please use PNG, JPEG, JPG or GIF.`, "error");
                            e.target.value = ""; return;
                          }
                          if (f.size > 2097152) {
                            pushToast(`"${f.name}" is ${(f.size/1048576).toFixed(1)} MB — over the 2 MB limit.`, "error");
                            setFieldErrors((p) => ({ ...p, _file: "File exceeds the 2 MB size limit." }));
                            e.target.value = ""; return;
                          }
                          setFieldErrors((p) => { const n={...p}; delete n._file; return n; });
                          setFile(f);
                        }}
                        className={`block w-full text-sm text-gray-600 file:mr-4 file:py-2.5 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 ${fieldErrors._file ? "ring-1 ring-red-500 rounded-md" : ""}`}
                        accept=".png,.jpeg,.jpg,.gif"
                      />
                      <FieldError message={fieldErrors._file} />
                      <p className="text-xs text-gray-500 mt-1">
                        Supported formats: PNG, JPEG, JPG, GIF · Max 2 MB
                      </p>
                    </div>
                  </div>

                  <div className="modal-footer flex justify-end p-6 border-t border-gray-200 rounded-b">
                    <Button
                      type="submit"
                      className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                      disabled={loading}
                    >
                      {loading ? (
                        <span className="flex items-center">
                          <svg
                            className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Creating...
                        </span>
                      ) : (
                        "Submit"
                      )}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

{/* Edit Modal */}
{showEditModal && (
  <div
    className="modal fade show fixed inset-0 overflow-y-auto"
    style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
    onClick={closeAllModals}
  >
    <div
      className="flex items-center justify-center min-h-screen"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="modal-dialog modal-lg relative w-auto my-6 mx-auto max-w-3xl">
        <div className="modal-content relative bg-white rounded-lg shadow-lg">
          <div className="modal-header flex items-start justify-between p-6 border-b rounded-t">
            <h5 className="modal-title text-xl font-semibold text-gray-800">
              Edit Profile
            </h5>
            <button
              onClick={closeAllModals}
              className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center"
            >
              <span className="la la-times text-2xl"></span>
            </button>
          </div>

          <form onSubmit={handleUpdateProfile}>
            <div className="modal-body p-6 space-y-6 max-h-[70vh] overflow-y-auto">
              {/* Basic Info */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Basic Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="form-group space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Full Name *
                    </label>
                    <Input
                      type="text"
                      name="full_name"
                      value={formData.full_name}
                      onChange={(e) => { handleInputChange(e); setFieldErrors((p) => { const n={...p}; delete n.full_name; return n; }); }}
                      className={`w-full p-3 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${fieldErrors.full_name ? "border-red-500 bg-red-50" : "border-gray-300"}`}
                    />
                    <FieldError message={fieldErrors.full_name} />
                  </div>

                  <div className="form-group space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Phone <span className="text-gray-400 font-normal text-xs">(optional)</span>
                    </label>
                    <Input
                      type="text"
                      name="phone"
                      value={formData.phone}
                      onChange={(e) => { handleInputChange(e); setFieldErrors((p) => { const n={...p}; delete n.phone; return n; }); }}
                      className={`w-full p-3 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${fieldErrors.phone ? "border-red-500 bg-red-50" : "border-gray-300"}`}
                    />
                    <FieldError message={fieldErrors.phone} />
                  </div>

                  <div className="form-group space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Email *
                    </label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={(e) => { handleInputChange(e); setFieldErrors((p) => { const n={...p}; delete n.email; return n; }); }}
                      className={`w-full p-3 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${fieldErrors.email ? "border-red-500 bg-red-50" : "border-gray-300"}`}
                    />
                    <FieldError message={fieldErrors.email} />
                  </div>

                  <div className="form-group space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Role *
                    </label>
                    <select
                      name="role"
                      value={formData.role.id || ""}
                      onChange={(e) => { handleInputChange(e); setFieldErrors((p) => { const n={...p}; delete n.role; return n; }); }}
                      className={`w-full p-3 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${fieldErrors.role ? "border-red-500 bg-red-50" : "border-gray-300"}`}
                    >
                      <option value="">Select a role</option>
                      {roles.map((role) => (
                        <option key={role.id} value={role.id}>
                          {role.designation}
                        </option>
                      ))}
                    </select>
                    <FieldError message={fieldErrors.role} />
                  </div>
                </div>
              </div>

              {/* Bio */}
              <div className="form-group space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Bio *
                </label>
                <Textarea
                  name="bio"
                  value={formData.bio}
                  onChange={(e) => { handleInputChange(e); setFieldErrors((p) => { const n={...p}; delete n.bio; return n; }); }}
                  rows="6"
                  className={`w-full p-3 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${fieldErrors.bio ? "border-red-500 bg-red-50" : "border-gray-300"}`}
                />
                <FieldError message={fieldErrors.bio} />
              </div>

              {/* Array Fields */}
              <div className="space-y-6">
                <h3 className="text-lg font-medium text-gray-900">
                  Professional Information
                </h3>

                {/* Practice Areas */}
                <div className="form-group space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Practice Areas *
                  </label>
                  <div className="flex gap-2">
                    <Input
                      type="text"
                      name="practice_areas"
                      value={tempArrayInput.practice_areas || ""}
                      onChange={handleArrayInputChange}
                      placeholder="Add practice area"
                      className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <button
                      type="button"
                      onClick={() => addArrayItem("practice_areas")}
                      style={{background:"#2563eb",color:"#fff",border:"none",borderRadius:"6px",padding:"8px 14px",cursor:"pointer",fontSize:"13px",fontWeight:"600",flexShrink:0,whiteSpace:"nowrap"}}
                    >
                      Add
                    </button>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {formData.practice_areas.map((area, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800"
                      >
                        {area}
                        <button
                          type="button"
                          onClick={() => removeArrayItem("practice_areas", index)}
                          className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full text-green-400 hover:bg-green-200 hover:text-green-500"
                        >
                          &times;
                        </button>
                      </span>
                    ))}
                  </div>
                  <FieldError message={fieldErrors.practice_areas} />
                </div>

                {/* Experience */}
                <div className="form-group space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Experience
                  </label>
                  <div className="flex gap-2">
                    <Input
                      type="text"
                      name="experience"
                      value={tempArrayInput.experience || ""}
                      onChange={handleArrayInputChange}
                      placeholder="Add experience item"
                      className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <button
                      type="button"
                      onClick={() => addArrayItem("experience")}
                      style={{background:"#2563eb",color:"#fff",border:"none",borderRadius:"6px",padding:"8px 14px",cursor:"pointer",fontSize:"13px",fontWeight:"600",flexShrink:0,whiteSpace:"nowrap"}}
                    >
                      Add
                    </button>
                  </div>
                  <div className="mt-2">
                    {formData.experience.map((item, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mr-1 mb-1"
                      >
                        {item}
                        <button
                          type="button"
                          onClick={() => removeArrayItem("experience", index)}
                          className="ml-1 text-blue-600 hover:text-blue-900"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                  <FieldError message={fieldErrors.experience} />
                </div>

                {/* Education */}
                <div className="form-group space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Education *
                  </label>
                  <div className="flex gap-2">
                    <Input
                      type="text"
                      name="education"
                      value={tempArrayInput.education || ""}
                      onChange={handleArrayInputChange}
                      placeholder="Add education item"
                      className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <button
                      type="button"
                      onClick={() => addArrayItem("education")}
                      style={{background:"#2563eb",color:"#fff",border:"none",borderRadius:"6px",padding:"8px 14px",cursor:"pointer",fontSize:"13px",fontWeight:"600",flexShrink:0,whiteSpace:"nowrap"}}
                    >
                      Add
                    </button>
                  </div>
                  <div className="mt-2">
                    {formData.education.map((item, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mr-1 mb-1"
                      >
                        {item}
                        <button
                          type="button"
                          onClick={() => removeArrayItem("education", index)}
                          className="ml-1 text-blue-600 hover:text-blue-900"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                  <FieldError message={fieldErrors.education} />
                </div>

                {/* Bars */}
                <div className="form-group space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Bar Admissions *
                  </label>
                  <div className="flex gap-2">
                    <Input
                      type="text"
                      name="bars"
                      value={tempArrayInput.bars || ""}
                      onChange={handleArrayInputChange}
                      placeholder="Add bar admission"
                      className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <button
                      type="button"
                      onClick={() => addArrayItem("bars")}
                      style={{background:"#2563eb",color:"#fff",border:"none",borderRadius:"6px",padding:"8px 14px",cursor:"pointer",fontSize:"13px",fontWeight:"600",flexShrink:0,whiteSpace:"nowrap"}}
                    >
                      Add
                    </button>
                  </div>
                  <div className="mt-2">
                    {formData.bars.map((item, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mr-1 mb-1"
                      >
                        {item}
                        <button
                          type="button"
                          onClick={() => removeArrayItem("bars", index)}
                          className="ml-1 text-blue-600 hover:text-blue-900"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                  <FieldError message={fieldErrors.bars} />
                </div>

                {/* Publications */}
                <div className="form-group space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Publications
                  </label>
                  <div className="flex gap-2">
                    <Input
                      type="text"
                      name="publications"
                      value={tempArrayInput.publications || ""}
                      onChange={handleArrayInputChange}
                      placeholder="Add publication"
                      className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <button
                      type="button"
                      onClick={() => addArrayItem("publications")}
                      style={{background:"#2563eb",color:"#fff",border:"none",borderRadius:"6px",padding:"8px 14px",cursor:"pointer",fontSize:"13px",fontWeight:"600",flexShrink:0,whiteSpace:"nowrap"}}
                    >
                      Add
                    </button>
                  </div>
                  <div className="mt-2">
                    {formData.publications.map((item, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mr-1 mb-1"
                      >
                        {item}
                        <button
                          type="button"
                          onClick={() => removeArrayItem("publications", index)}
                          className="ml-1 text-blue-600 hover:text-blue-900"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                  <FieldError message={fieldErrors.publications} />
                </div>

                {/* Certifications */}
                <div className="form-group space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Certifications
                  </label>
                  <div className="flex gap-2">
                    <Input
                      type="text"
                      name="certifications"
                      value={tempArrayInput.certifications || ""}
                      onChange={handleArrayInputChange}
                      placeholder="Add certification"
                      className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <button
                      type="button"
                      onClick={() => addArrayItem("certifications")}
                      style={{background:"#2563eb",color:"#fff",border:"none",borderRadius:"6px",padding:"8px 14px",cursor:"pointer",fontSize:"13px",fontWeight:"600",flexShrink:0,whiteSpace:"nowrap"}}
                    >
                      Add
                    </button>
                  </div>
                  <div className="mt-2">
                    {formData.certifications.map((item, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mr-1 mb-1"
                      >
                        {item}
                        <button
                          type="button"
                          onClick={() => removeArrayItem("certifications", index)}
                          className="ml-1 text-blue-600 hover:text-blue-900"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                  <FieldError message={fieldErrors.certifications} />
                </div>

                {/* Books */}
                <div className="form-group space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Books
                  </label>
                  <div className="flex gap-2">
                    <select
                      name="books"
                      value={tempArrayInput.books || ""}
                      onChange={handleArrayInputChange}
                      className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select a book</option>
                      {booksList.map((book) => (
                        <option key={book.id} value={book.id}>
                          {book.name} by {book.author}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => addArrayItem("books")}
                      style={{background:"#2563eb",color:"#fff",border:"none",borderRadius:"6px",padding:"8px 14px",cursor:"pointer",fontSize:"13px",fontWeight:"600",flexShrink:0,whiteSpace:"nowrap"}}
                    >
                      Add
                    </button>
                  </div>
                  <div className="mt-2">
                    {formData.books.map((item, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mr-1 mb-1"
                      >
                        {item.name} by {item.author}
                        <button
                          type="button"
                          onClick={() => removeArrayItem("books", index)}
                          className="ml-1 text-blue-600 hover:text-blue-900"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                  <FieldError message={fieldErrors.books} />
                </div>

                {/* Memberships */}
                <div className="form-group space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Memberships *
                  </label>
                  <div className="flex gap-2">
                    <Input
                      type="text"
                      name="memberships"
                      value={tempArrayInput.memberships || ""}
                      onChange={handleArrayInputChange}
                      placeholder="Add membership"
                      className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <button
                      type="button"
                      onClick={() => addArrayItem("memberships")}
                      style={{background:"#2563eb",color:"#fff",border:"none",borderRadius:"6px",padding:"8px 14px",cursor:"pointer",fontSize:"13px",fontWeight:"600",flexShrink:0,whiteSpace:"nowrap"}}
                    >
                      Add
                    </button>
                  </div>
                  <div className="mt-2">
                    {formData.memberships.map((item, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mr-1 mb-1"
                      >
                        {item}
                        <button
                          type="button"
                          onClick={() => removeArrayItem("memberships", index)}
                          className="ml-1 text-blue-600 hover:text-blue-900"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                  <FieldError message={fieldErrors.memberships} />
                </div>
              </div>

              {/* File Upload Section */}
              <div className="form-group space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Upload Profile Picture
                </label>
                <input
                  type="file"
                  onChange={(e) => {
                    const f = e.target.files[0];
                    if (!f) return;
                    const allowed = ["image/png","image/jpeg","image/jpg","image/gif"];
                    if (!allowed.includes(f.type)) {
                      pushToast(`"${f.name}" is not a supported type. Please use PNG, JPEG, JPG or GIF.`, "error");
                      e.target.value = ""; return;
                    }
                    if (f.size > 2097152) {
                      pushToast(`"${f.name}" is ${(f.size/1048576).toFixed(1)} MB — over the 2 MB limit.`, "error");
                      setFieldErrors((p) => ({ ...p, _file: "File exceeds the 2 MB size limit." }));
                      e.target.value = ""; return;
                    }
                    setFieldErrors((p) => { const n={...p}; delete n._file; return n; });
                    setFile(f);
                  }}
                  className={`block w-full text-sm text-gray-600 file:mr-4 file:py-2.5 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 ${fieldErrors._file ? "ring-1 ring-red-500 rounded-md" : ""}`}
                  accept=".png,.jpeg,.jpg,.gif"
                />
                <FieldError message={fieldErrors._file} />
                <p className="text-xs text-gray-500 mt-1">
                  {file
                    ? `Selected file: ${file.name}`
                    : currentProfile?.file_url
                    ? `Current file: ${currentProfile.file_url.split('/').pop()}`
                    : "No file currently attached"}
                </p>
                <p className="text-xs text-gray-500">
                  Supported formats: PNG, JPEG, JPG, GIF · Max 2 MB
                </p>
              </div>
            </div>

            <div className="modal-footer flex justify-end p-6 border-t border-gray-200 rounded-b space-x-4">
              <Button
                type="button"
                onClick={closeAllModals}
                className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
              >
                Close
              </Button>
              <Button
                type="submit"
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                disabled={loading}
              >
                {loading ? (
                  <span className="flex items-center">
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Updating...
                  </span>
                ) : (
                  "Update Profile"
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
)}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div
          className="modal fade show fixed inset-0 overflow-y-auto"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={closeAllModals}
        >
          <div
            className="flex items-center justify-center min-h-screen"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Confirm Delete</h5>
                  <button
                    onClick={closeAllModals}
                    className="btn-close text-red-500 dark:text-red-500 hover:text-red-700"
                  ></button>
                </div>
                <div className="modal-body">
                  <p>
                    Are you sure you want to delete this profile? This action
                    cannot be undone.
                  </p>
                </div>
                <div className="modal-footer flex justify-center gap-4">
                  <button
                    onClick={closeAllModals}
                    className="btn bg-red-500 text-white hover:bg-red-700 px-6"
                  >
                    Cancel
                  </button>
                  <Button
                    color="danger"
                    onClick={handleDeleteProfile}
                    className="btn bg-red-500 text-white hover:bg-red-700 px-6"
                    disabled={loading}
                  >
                    {loading ? "Deleting..." : "Delete Profile"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && currentProfile && (
        <div
          className="modal fade show fixed inset-0 overflow-y-auto"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="flex items-center justify-center min-h-screen">
            <div className="modal-dialog relative w-auto my-6 mx-auto max-w-4xl">
              <div className="modal-content relative bg-white rounded-lg shadow-lg">
                <div className="modal-header flex items-start justify-between p-6 border-b rounded-t">
                  <div>
                    <h5 className="modal-title text-2xl font-bold text-gray-800">
                      {currentProfile.full_name}
                    </h5>
                    <p className="text-gray-600 mt-1">
                      {currentProfile.role?.designation || "No role specified"}
                    </p>
                  </div>
                  <button
                    onClick={closeAllModals}
                    className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center"
                  >
                    <span className="la la-times text-2xl"></span>
                  </button>
                </div>

                <div className="modal-body p-6 max-h-[80vh] overflow-y-auto">
                  {/* Profile Image URL Section */}
                  <div className="mb-8 bg-gray-50 p-4 rounded-lg">
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="text-lg font-semibold text-gray-800">
                        Profile Image
                      </h3>
                    </div>
                    {currentProfile.file_url ? (
                      <div className="flex items-center space-x-4">
                        <input
                          type="text"
                          value={currentProfile.file_url}
                          readOnly
                          className="flex-1 p-2 border border-gray-300 rounded-md bg-gray-50 text-sm"
                        />
                        <div className="flex space-x-2">
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(
                                currentProfile.file_url
                              );
                              // Add toast notification here if needed
                            }}
                            className="p-2 bg-blue-100 text-blue-600 rounded-md hover:bg-blue-200"
                            title="Copy URL"
                          >
                            <span className="la la-copy"></span>
                          </button>
                          <a
                            href={currentProfile.file_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 bg-green-100 text-green-600 rounded-md hover:bg-green-200"
                            title="Open in new tab"
                          >
                            <span className="la la-external-link-alt"></span>
                          </a>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 italic">
                        No image uploaded
                      </p>
                    )}
                  </div>

                  {/* Personal Information Section */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h3 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">
                        Personal Information
                      </h3>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-500">
                            Email
                          </label>
                          <p className="mt-1 text-sm text-gray-900">
                            {currentProfile.email || "Not specified"}
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-500">
                            Phone
                          </label>
                          <p className="mt-1 text-sm text-gray-900">
                            {currentProfile.phone || "Not specified"}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Bio Section */}
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h3 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">
                        Bio
                      </h3>
                      <p className="text-gray-700 whitespace-pre-line">
                        {currentProfile.bio || "No bio available."}
                      </p>
                    </div>
                  </div>

                  {/* Professional Information Sections */}
                  <div className="space-y-6">
                    {/* Practice Areas */}
                    {currentProfile.practice_areas?.length > 0 && (
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h3 className="text-lg font-semibold text-gray-800 mb-3 border-b pb-2">
                          Practice Areas
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          {renderPracticeAreas(currentProfile.practice_areas)}
                        </div>
                      </div>
                    )}

                    {/* Education */}
                    {currentProfile.education?.length > 0 && (
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h3 className="text-lg font-semibold text-gray-800 mb-3 border-b pb-2">
                          Education
                        </h3>
                        <ul className="space-y-2">
                          {currentProfile.education.map((edu, index) => (
                            <li key={index} className="flex items-start">
                              <span className="text-gray-500 mr-2">•</span>
                              <span className="text-gray-700">{edu}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Experience */}
                    {currentProfile.experience?.length > 0 && (
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h3 className="text-lg font-semibold text-gray-800 mb-3 border-b pb-2">
                          Experience
                        </h3>
                        <ul className="space-y-2">
                          {currentProfile.experience.map((exp, index) => (
                            <li key={index} className="flex items-start">
                              <span className="text-gray-500 mr-2">•</span>
                              <span className="text-gray-700">{exp}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Bar Admissions */}
                    {currentProfile.bars?.length > 0 && (
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h3 className="text-lg font-semibold text-gray-800 mb-3 border-b pb-2">
                          Bar Admissions
                        </h3>
                        <ul className="space-y-2">
                          {currentProfile.bars.map((bar, index) => (
                            <li key={index} className="flex items-start">
                              <span className="text-gray-500 mr-2">•</span>
                              <span className="text-gray-700">{bar}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Certifications */}
                    {currentProfile.certifications?.length > 0 && (
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h3 className="text-lg font-semibold text-gray-800 mb-3 border-b pb-2">
                          Certifications
                        </h3>
                        <ul className="space-y-2">
                          {currentProfile.certifications.map((cert, index) => (
                            <li key={index} className="flex items-start">
                              <span className="text-gray-500 mr-2">•</span>
                              <span className="text-gray-700">{cert}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}


                    {/* Books */}
                    {currentProfile.books?.length > 0 && (
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h3 className="text-lg font-semibold text-gray-800 mb-3 border-b pb-2">
                          Books
                        </h3>
                        <div className="space-y-3">
                          {renderBooks(currentProfile.books)}
                        </div>
                      </div>
                    )}

                    {/* Memberships */}
                    {currentProfile.memberships?.length > 0 && (
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h3 className="text-lg font-semibold text-gray-800 mb-3 border-b pb-2">
                          Memberships
                        </h3>
                        <ul className="space-y-2">
                          {currentProfile.memberships.map((mem, index) => (
                            <li key={index} className="flex items-start">
                              <span className="text-gray-500 mr-2">•</span>
                              <span className="text-gray-700">{mem}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}


                    {/* Publications */}
                    {currentProfile.publications?.length > 0 && (
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h3 className="text-lg font-semibold text-gray-800 mb-3 border-b pb-2">
                          Publications
                        </h3>
                        <ul className="space-y-2">
                          {currentProfile.publications.map((pub, index) => (
                            <li key={index} className="flex items-start">
                              <span className="text-gray-500 mr-2">•</span>
                              <span className="text-gray-700">{pub}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}


                  </div>
                </div>

                <div className="modal-footer flex justify-end p-4 border-t gap-4">
                  <Button
                    onClick={closeAllModals}
                    className="btn bg-gray-300 text-gray-700 hover:bg-gray-400 px-6"
                  >
                    Close
                  </Button>
                  <Button
                    onClick={() => {
                      closeAllModals();
                      openEditModal(currentProfile);
                    }}
                    className="btn bg-blue-600 text-white hover:bg-blue-700 px-6"
                  >
                    Edit Profile
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}



      <Footer />
    </main>
  );
};

export default Profiles;